/** Mittlerer Erdradius (IUGG) in Metern. */
const R_EARTH_M = 6_371_008.8;
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
/**
 * Projiziert einen Geo-Punkt in lokale Meter (x = Ost, y = Nord) relativ zum
 * Ursprung. Equirektangulär mit cos(Breite) des Ursprungs als Längen-Skalierung.
 *
 * Konventionen/Einheiten: Eingabe `latitude`/`longitude` in Dezimalgrad (WGS84-
 * konform angenommen); Ausgabe-Vec2 in METERN. Achsen: +x = Ost, +y = Nord
 * (rechtshändig, mathematisch positiv). Höhe/Elevation wird ignoriert — dies ist
 * eine rein 2D-Grundriss-Projektion (siehe Datei-Kopf zur Genauigkeitsgrenze).
 */
export function toLocalMeters(point, origin) {
    const lat0 = origin.latitude * DEG2RAD;
    const dLat = (point.latitude - origin.latitude) * DEG2RAD;
    const dLng = (point.longitude - origin.longitude) * DEG2RAD;
    return {
        x: dLng * Math.cos(lat0) * R_EARTH_M,
        y: dLat * R_EARTH_M,
    };
}
/**
 * Umkehrung von {@link toLocalMeters}: lokale Meter (x = Ost, y = Nord) → Geo-Punkt.
 * Eingabe-Vec2 in Metern, Ausgabe `latitude`/`longitude` in Dezimalgrad.
 *
 * Hinweis (bewusst zurückgestellt): Eine höhen-/elevationsbewusste Variante
 * (3D: x/y/z bzw. Hangneigung über DGM) ist hier NICHT enthalten — sie würde
 * eine z-Komponente in den Signaturen erfordern und ist nicht selbst-enthalten
 * gegenüber der bestehenden 2D-Vec2-Basis. Empfehlung: bei Bedarf als separater
 * Typ/Funktion (z.B. `Vec3`/`toLocalMeters3D`) additiv ergänzen, statt diese
 * Signaturen zu ändern. Die Dachneigung wird heute über {@link trueAreaM2} /
 * {@link slopeLengthM} (Korrektur A/cos β) abgebildet.
 */
export function fromLocalMeters(vec, origin) {
    const lat0 = origin.latitude * DEG2RAD;
    return {
        latitude: origin.latitude + (vec.y / R_EARTH_M) * RAD2DEG,
        longitude: origin.longitude + (vec.x / (R_EARTH_M * Math.cos(lat0))) * RAD2DEG,
    };
}
/** Großkreis-Distanz (Haversine) in Metern. Genauer als die ebene Näherung. */
export function haversineM(a, b) {
    const phi1 = a.latitude * DEG2RAD;
    const phi2 = b.latitude * DEG2RAD;
    const dPhi = (b.latitude - a.latitude) * DEG2RAD;
    const dLambda = (b.longitude - a.longitude) * DEG2RAD;
    const s = Math.sin(dPhi / 2) ** 2 +
        Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
    return 2 * R_EARTH_M * Math.asin(Math.min(1, Math.sqrt(s)));
}
/**
 * Dachneigungs-Korrektur: rechnet eine in der Horizontalebene gemessene
 * (projizierte) Fläche auf die echte, geneigte Dachfläche hoch: A₃D = A / cos(β).
 * @param projectedAreaM2 horizontal projizierte Fläche (m²)
 * @param tiltDeg Dachneigung in Grad, [0, 90)
 */
export function trueAreaM2(projectedAreaM2, tiltDeg) {
    assertTilt(tiltDeg);
    return projectedAreaM2 / Math.cos(tiltDeg * DEG2RAD);
}
/**
 * Wahre Schräglänge entlang der Falllinie aus der horizontalen Länge:
 * L_schräg = L_horizontal / cos(β). Grundlage für die Reihen-Dimension im Raster.
 */
export function slopeLengthM(horizontalLengthM, tiltDeg) {
    assertTilt(tiltDeg);
    return horizontalLengthM / Math.cos(tiltDeg * DEG2RAD);
}
function assertTilt(tiltDeg) {
    if (!Number.isFinite(tiltDeg) || tiltDeg < 0 || tiltDeg >= 90) {
        throw new RangeError(`tiltDeg muss in [0, 90) liegen, war ${tiltDeg}`);
    }
}
//# sourceMappingURL=projection.js.map